import React, { useCallback, useEffect, useRef, useState } from "react";
import { useLocation } from "react-router-dom";
import { useDispatch, useSelector } from "react-redux";
import { toast } from "react-toastify";

import { RootState, AppDispatch } from "../../store/store";
import {
  toggleWidget,
  closeWidget,
  appendMessage,
  setMessages,
  setMessageFeedback,
  setThreadId,
  setCompanyId,
  setNeedsCompany,
  setAwaitingReply,
  resetWidgetConversation,
  setThreads,
  setShowHistory,
  toggleHistory,
  setDocuments,
  addDocuments,
  removeDocument,
  setUploading,
  setWidgetPosition,
  resetWidget,
} from "../../store/dimeAiWidgetSlice";
import {
  sendDimeAiMessage,
  getDimeAiThreads,
  getDimeAiThread,
  getDimeAiDocuments,
  uploadDimeAiDocuments,
  discardDimeAiDocument,
  sendDimeAiFeedback,
  DimeAIThread,
  ChatDocument,
} from "../../services/https/dimeai";
import AnswerBody from "../../pages/dimeai/AnswerBody";
import { images } from "../../assets/images";

/**
 * Splits the system-appended caveat off the end of an answer's text.
 *
 * Same helper as DimeAI.tsx's splitCaveat, duplicated rather than shared
 * because the two already duplicate their own openThread/textOf logic for
 * the same reason — the widget's message shape is its own Redux type, not
 * DimeAI.tsx's local one.
 */
const splitCaveat = (
  text: string,
  caveat: string | null | undefined,
): { body: string; caveat: string | null } => {
  if (!caveat) return { body: text, caveat: null };
  const needle = "\n\n" + caveat;
  return text.endsWith(needle)
    ? { body: text.slice(0, text.length - needle.length), caveat }
    : { body: text, caveat: null };
};
import { useThinkingStage } from "../../hooks";

// How the widget watches for an answer the request stopped waiting for. Same
// cadence and ceiling as the full DimeAI page (RESUME_POLL_MS, PENDING_MAX_MS).
const WATCH_POLL_MS = 3000;
const WATCH_MAX_MS = 10 * 60 * 1000;

// AnswerBody renders plain classNames (dimeai-answer-para, dimeai-answer-list,
// dimeai-table, dimeai-tooltip, dimeai-chart, ...) with no styling of its
// own — every one of those rules lives in DimeAI.css. That file only enters
// the document when the full DimeAI page's lazy route chunk loads
// (routers/index.ts: `lazy(() => import("../pages/dimeai/DimeAI"))`), so
// opening the widget on any other page first — the ordinary case, since it's
// mounted at the app root — rendered every assistant reply with zero
// structure: run-together paragraphs, unstyled bold, no list markers, an
// unstyled table. This import is read-only (no edits to DimeAI.css itself)
// and just makes sure those rules are present regardless of which page the
// widget happens to be open on.
import "../../pages/dimeai/DimeAI.css";
import "./DimeAiWidget.css";

/**
 * The DimeAI mark, pulsing — the exact same icon used in the sidebar
 * (images.AI / DimeAI_Icon.png), not a redrawn shape, so the floating
 * bubble reads as "the same DimeAI" rather than a new abstract identity.
 * A soft glow breathes behind it and the icon itself brightens slightly
 * on the same beat; `thinking` speeds both up as the "generating a
 * reply" cue — no separate three-dot spinner.
 *
 * The glow needs something dark behind it to read against — on the
 * dark theme the app's own background did that job for free, but on
 * light pages/panels an emerald glow on white has almost no contrast
 * and nearly disappears. `dimeai-brain-backdrop` is a small fixed-dark
 * disc carried by the mark itself, so the pulse looks the same (and is
 * actually visible) regardless of what it's floating over.
 */
const DimeAiBrain: React.FC<{ className?: string; thinking?: boolean }> = ({
  className = "",
  thinking = false,
}) => (
  <div
    className={`dimeai-brain${thinking ? " dimeai-brain--thinking" : ""}${
      className ? ` ${className}` : ""
    }`}
  >
    <div className="dimeai-brain-backdrop" aria-hidden="true" />
    <img
      className="dimeai-brain-glow"
      src={images.AI}
      alt=""
      aria-hidden="true"
      draggable={false}
    />
    <img className="dimeai-brain-icon" src={images.AI} alt="DimeAI" draggable={false} />
  </div>
);

/**
 * Floating DimeAI chat widget — Phase 1 (bubble + basic send/receive).
 *
 * Mounted once at the App root (see App.tsx), as a sibling to <AppRoutes />,
 * so it survives navigation between pages instead of remounting the way
 * anything inside MainLayout/PrivateRoute would (routes here are flat
 * siblings, not nested under a shared layout route — see the research note
 * in the task history). State lives in Redux for the same reason.
 *
 * It reuses the same backend threads/messages the full DimeAI page
 * (src/pages/dimeai/DimeAI.tsx) reads and writes via services/https/dimeai.ts
 * — nothing here is a parallel conversation store. A message sent from the
 * widget shows up in the full page's History list and vice versa, because
 * both are just clients of the same thread_id.
 *
 * Deliberately does NOT touch DimeAI.tsx, DimeAI.css, AnswerBody.tsx, or
 * dimeai.ts — those are actively being worked on elsewhere. This component
 * only imports from them (read-only) and adds its own separate UI.
 *
 * Phase 2 adds a history panel (past threads, same /ai-advisor/threads/
 * list the full page reads) and attachment upload (same /ai-advisor/
 * documents endpoints), both reusing the exact functions DimeAI.tsx
 * already calls in services/https/dimeai.ts — no new backend contract.
 */
const DimeAiWidget: React.FC = () => {
  const dispatch = useDispatch<AppDispatch>();
  const location = useLocation();

  const { user, loginDetails } = useSelector((state: RootState) => state.user);
  const {
    isOpen,
    messages,
    threadId,
    companyId,
    needsCompany,
    awaitingReply,
    threads,
    showHistory,
    documents,
    uploading,
    position,
  } = useSelector((state: RootState) => state.dimeAiWidget);

  const [input, setInput] = useState("");
  const inputRef = useRef<HTMLTextAreaElement>(null);
  const scrollRef = useRef<HTMLDivElement>(null);
  const fileRef = useRef<HTMLInputElement>(null);
  // Client-side object URLs for image attachments, keyed by document id --
  // same mechanism and reasoning as the full DimeAI page (DimeAI.tsx):
  // built from the picked/pasted/dropped File itself so the preview shows
  // instantly, kept local rather than in Redux since a blob URL only means
  // anything in this browser tab. Documents/spreadsheets never get one.
  const [previewUrls, setPreviewUrls] = useState<Record<string, string>>({});
  const previewUrlsRef = useRef(previewUrls);
  previewUrlsRef.current = previewUrls;
  useEffect(() => {
    return () => {
      Object.values(previewUrlsRef.current).forEach((url) =>
        URL.revokeObjectURL(url),
      );
    };
  }, []);
  // Same double-send guard as DimeAI.tsx: state alone isn't enough because
  // it doesn't flip until the next render, so two Enter presses in one tick
  // can both read "not sending" and both fire.
  const inFlight = useRef(false);
  // Bumped whenever the on-screen conversation is replaced (New chat or a
  // past thread opened). An in-flight reply compares its captured value
  // against this and discards itself if the conversation moved on,
  // mirroring DimeAI.tsx's own guard.
  const conversation = useRef(0);

  // Rotating "what's happening" label for the typing bubble below, driven by
  // the same awaitingReply flag that already gates its visibility.
  const thinkingLabel = useThinkingStage(awaitingReply);

  // ---- drag-to-reposition (bubble handle) ----
  // Tracks an in-progress drag gesture started on the launcher. Kept out of
  // Redux/state because it only matters between pointerdown and pointerup —
  // re-rendering on every pixel of movement would be wasteful, so the drag
  // itself is done by writing directly to the DOM node and only committing
  // the final position to Redux (setWidgetPosition) on release.
  //
  // The live-drag feedback is applied as a `transform: translate3d(...)`
  // offset, NOT by writing `right`/`bottom` directly (which an earlier
  // version did). `right`/`bottom` are also what the JSX `style` prop below
  // computes from Redux `position` — so mutating them imperatively put that
  // mutation in a race with React's own reconciliation of the same two CSS
  // properties. Any unrelated re-render of this component mid-drag (a
  // streaming reply token, the typing indicator, an unread-count tick — all
  // live in the same Redux slice this component subscribes to) could
  // reapply the still-stale Redux-derived right/bottom and stomp the
  // in-progress drag position back, which is what made dragging feel sticky
  // and laggy. `transform` is a CSS property React never touches here, so
  // there's nothing for a re-render to fight over; right/bottom are only
  // ever written once, to Redux, when the gesture ends.
  const dragRef = useRef<{
    startX: number;
    startY: number;
    startRight: number;
    startBottom: number;
    moved: boolean;
  } | null>(null);
  const widgetRef = useRef<HTMLDivElement>(null);

  const clamp = (value: number, max: number) =>
    Math.min(Math.max(value, 8), Math.max(8, max));

  const handleBubblePointerDown = (e: React.PointerEvent<HTMLButtonElement>) => {
    // Only the primary button/touch starts a drag — a right-click or a
    // secondary pointer shouldn't move the widget.
    if (e.button !== undefined && e.button !== 0) return;
    // Belt-and-suspenders alongside draggable={false} on the <img> tags
    // inside DimeAiBrain: without this, some browsers still start their
    // own native "drag this image out of the page" gesture on a
    // mousedown-and-move over an <img>, which renders its own translucent
    // ghost preview on top of the one we're positioning ourselves — the
    // "second, weird-looking brain" that showed up alongside the real one.
    e.preventDefault();
    const startRight = position?.right ?? 24;
    const startBottom = position?.bottom ?? 24;
    dragRef.current = {
      startX: e.clientX,
      startY: e.clientY,
      startRight,
      startBottom,
      moved: false,
    };

    const handlePointerMove = (ev: PointerEvent) => {
      const drag = dragRef.current;
      if (!drag) return;
      const dx = ev.clientX - drag.startX;
      const dy = ev.clientY - drag.startY;
      // Below this, it's a click, not a drag — matters for the click
      // handler below, which must not toggle the panel open/closed for a
      // gesture that was actually a drag.
      if (!drag.moved && Math.hypot(dx, dy) > 4) drag.moved = true;
      if (!drag.moved) return;

      const node = widgetRef.current;
      const maxRight = (node ? window.innerWidth - node.offsetWidth : window.innerWidth) - 8;
      const maxBottom = (node ? window.innerHeight - node.offsetHeight : window.innerHeight) - 8;
      const nextRight = clamp(drag.startRight - dx, maxRight);
      const nextBottom = clamp(drag.startBottom - dy, maxBottom);
      if (node) {
        // Pixel offset from the drag's starting position, clamped to the
        // same bounds as nextRight/nextBottom above — a positive shift
        // moves the element right/down on screen, matching the sign of a
        // shrinking `right`/`bottom`.
        const shiftX = drag.startRight - nextRight;
        const shiftY = drag.startBottom - nextBottom;
        node.style.transform = `translate3d(${shiftX}px, ${shiftY}px, 0)`;
      }
    };

    const handlePointerUp = (ev: PointerEvent) => {
      window.removeEventListener("pointermove", handlePointerMove);
      window.removeEventListener("pointerup", handlePointerUp);
      const drag = dragRef.current;
      const node = widgetRef.current;
      if (drag?.moved) {
        const dx = ev.clientX - drag.startX;
        const dy = ev.clientY - drag.startY;
        const maxRight = (node ? window.innerWidth - node.offsetWidth : window.innerWidth) - 8;
        const maxBottom = (node ? window.innerHeight - node.offsetHeight : window.innerHeight) - 8;
        dispatch(
          setWidgetPosition({
            right: clamp(drag.startRight - dx, maxRight),
            bottom: clamp(drag.startBottom - dy, maxBottom),
          }),
        );
      }
      // Drop the temporary drag transform now that the real position is
      // (about to be) committed to Redux and will land in the same spot via
      // right/bottom — clearing it a frame early would cause a visible
      // snap-back, but React batches this dispatch's re-render, so by the
      // time the browser paints, both changes land together.
      if (node) node.style.transform = "";
      // Cleared on a short delay rather than immediately: the click handler
      // below fires right after pointerup for a real click, and needs to
      // still see `moved` for this same gesture to suppress the toggle.
      setTimeout(() => {
        dragRef.current = null;
      }, 0);
    };

    window.addEventListener("pointermove", handlePointerMove);
    window.addEventListener("pointerup", handlePointerUp);
  };

  const handleBubbleClick = () => {
    if (dragRef.current?.moved) return; // this gesture was a drag, not a click
    dispatch(toggleWidget());
  };

  const companies = user?.companies || [];
  const isAuthenticated = Boolean(
    user?.id || loginDetails?.access_token,
  );

  // Cross-user leak guard. This widget is mounted once at the App root and
  // its state lives in Redux specifically so it survives navigation — but
  // that same design means it also survives a *logout*, since logging out
  // only clears the user slice (see Header.tsx/useIdleTimer.ts), never this
  // one. Without this, User A's messages, thread list and attached
  // documents stay in dimeAiWidgetSlice and are shown to User B the moment
  // they log into the same browser tab. Rather than editing every logout
  // call site (shared code other people are actively changing), watch the
  // one signal that actually means "the identity behind this widget
  // changed": user?.id flipping to a different value, including to/from
  // undefined on logout/login. Fires once per real transition, never on an
  // ordinary render with the same id.
  const lastUserIdRef = useRef(user?.id);
  useEffect(() => {
    if (lastUserIdRef.current !== user?.id) {
      lastUserIdRef.current = user?.id;
      dispatch(resetWidget());
    }
  }, [user?.id, dispatch]);

  // Hidden on the full DimeAI page itself (that page already is the chat —
  // stacking a second one on top of it would be confusing, not helpful),
  // on the Support page (has its own live-chat widget — Tawk — so two
  // floating chat bubbles would compete for the same corner), and whenever
  // the user isn't signed in yet (login, forgot-password, etc.).
  const isOnDimeAiPage = location.pathname.includes("/dime-ai/");
  const isOnSupportPage = location.pathname.includes("/support/");
  const shouldRender = isAuthenticated && !isOnDimeAiPage && !isOnSupportPage;

  useEffect(() => {
    scrollRef.current?.scrollTo({ top: scrollRef.current.scrollHeight });
  }, [messages, awaitingReply]);

  useEffect(() => {
    const node = inputRef.current;
    if (!node) return;
    node.style.height = "auto";
    node.style.height = `${node.scrollHeight}px`;
  }, [input]);

  // QA note this mirrors from DimeAI.tsx: only worth showing when someone
  // actually has more than one organisation — otherwise it's the same name
  // on every row and just adds noise.
  const companyName = useCallback(
    (id: string | null) =>
      (companies.length > 1 && id
        ? companies.find((c) => c.id === id)?.name
        : null) ?? null,
    [companies],
  );

  const loadThreads = useCallback(async () => {
    const res = await getDimeAiThreads();
    if (res?.status === 200 && res?.data?.success) {
      dispatch(setThreads(res.data.data || []));
    }
  }, [dispatch]);

  useEffect(() => {
    if (isAuthenticated) loadThreads();
  }, [isAuthenticated, loadThreads]);

  const loadDocuments = useCallback(
    async (id: string) => {
      const res = await getDimeAiDocuments(id);
      if (res?.status === 200 && res?.data?.success) {
        dispatch(
          setDocuments((res.data.data || []).filter((d: ChatDocument) => d.available)),
        );
      } else if (res?.status === 400 || res?.status === 404) {
        // The persisted threadId (rehydrated from storage on mount, or
        // carried across a remount as the widget is hidden/shown while
        // navigating) points at a thread that's stale or no longer owned
        // by this user. Clear it now so the widget stops refetching it on
        // every subsequent mount/navigation.
        dispatch(setThreadId(null));
        dispatch(setDocuments([]));
      }
    },
    [dispatch],
  );

  useEffect(() => {
    if (threadId) loadDocuments(threadId);
    else dispatch(setDocuments([]));
  }, [threadId, loadDocuments, dispatch]);

  const chooseCompany = useCallback(
    (id: string) => {
      dispatch(setCompanyId(id || null));
      dispatch(setNeedsCompany(false));
    },
    [dispatch],
  );

  const handleNewChat = () => {
    conversation.current += 1;
    dispatch(resetWidgetConversation());
    dispatch(setShowHistory(false));
    setInput("");
  };

  const handleToggleHistory = () => {
    if (!showHistory) loadThreads();
    dispatch(toggleHistory());
  };

  const openThread = async (thread: DimeAIThread) => {
    const res = await getDimeAiThread(thread.id);
    if (res?.status === 200 && res?.data?.success) {
      // Same content-shape assumption as DimeAI.tsx's own openThread: a
      // thread's stored messages are blocks, not a plain string — only the
      // text blocks are shown here, matching what the full page renders.
      const turns = (res.data.data.messages || [])
        .filter((m: any) => m.role === "user" || m.role === "assistant")
        .map((m: any) => {
          const text = (m.content || [])
            .filter((block: any) => block.type === "text")
            .map((block: any) => block.text)
            .join(" ");
          const { body, caveat } = splitCaveat(text, m.meta?.caveats);
          return {
            role: m.role,
            content: body,
            caveat,
            id: m.id,
            feedback: m.feedback_rating ?? null,
          };
        })
        .filter((m: { content: string }) => m.content.trim());

      conversation.current += 1;
      dispatch(resetWidgetConversation());
      dispatch(setMessages(turns));
      dispatch(setThreadId(thread.id));
      dispatch(setShowHistory(false));

      // Put the picker on the organisation this conversation was actually
      // held about, including back to blank — same reasoning as DimeAI.tsx:
      // inheriting the last-viewed company would answer from the wrong books.
      const company = res.data.data.thread?.company_id ?? null;
      dispatch(setCompanyId(company));
      if (company) dispatch(setNeedsCompany(false));
    } else {
      toast.error(res?.data?.message || "Couldn't open that conversation.");
    }
  };

  const handleAttachMany = async (files: File[]) => {
    dispatch(
      setUploading(files.length === 1 ? files[0].name : `${files.length} files`),
    );
    // Same matching-by-filename approach as DimeAI.tsx -- built before the
    // upload starts, matched to the server's response afterward.
    const pickedImageUrls = new Map<string, string>();
    files.forEach((file) => {
      if (file.type.startsWith("image/")) {
        pickedImageUrls.set(file.name, URL.createObjectURL(file));
      }
    });
    const res = await uploadDimeAiDocuments(threadId, files, companyId);
    dispatch(setUploading(null));

    if (res?.status === 201 && res?.data?.success) {
      const {
        attached,
        failed,
        thread_id: newThreadId,
      }: {
        attached: ChatDocument[];
        failed: { filename: string; detail: string }[];
        thread_id?: string;
      } = res.data.data;

      if (!threadId && newThreadId) dispatch(setThreadId(newThreadId));
      if (attached.length) {
        dispatch(addDocuments(attached));
        const usedNames = new Set<string>();
        const matched: Record<string, string> = {};
        attached.forEach((doc) => {
          if (doc.kind === "image" && pickedImageUrls.has(doc.filename)) {
            matched[doc.id] = pickedImageUrls.get(doc.filename)!;
            usedNames.add(doc.filename);
          }
        });
        if (Object.keys(matched).length) {
          setPreviewUrls((prev) => ({ ...prev, ...matched }));
        }
        pickedImageUrls.forEach((url, name) => {
          if (!usedNames.has(name)) URL.revokeObjectURL(url);
        });
        toast.success(
          attached.length === 1
            ? `${attached[0].filename} is ready — ask me about it.`
            : `${attached.length} files are ready — ask me about them.`,
        );
      }
      failed.forEach((f) => toast.error(`${f.filename}: ${f.detail}`));
    } else {
      pickedImageUrls.forEach((url) => URL.revokeObjectURL(url));
      const reason =
        res?.data?.detail || res?.data?.message || "Those files could not be read.";
      if (res?.data?.stillWorking) toast.info?.(reason);
      else toast.error(reason);
    }
  };

  const handleDiscard = async (document: ChatDocument) => {
    if (!threadId) return;
    dispatch(removeDocument(document.id));
    setPreviewUrls((prev) => {
      if (!(document.id in prev)) return prev;
      URL.revokeObjectURL(prev[document.id]);
      const { [document.id]: _discarded, ...rest } = prev;
      return rest;
    });
    const res = await discardDimeAiDocument(threadId, document.id);
    if (!(res?.status === 200 && res?.data?.success)) {
      toast.error("Couldn't remove that file.");
      loadDocuments(threadId);
    }
  };

  // Optimistic, same as the full DimeAI page's own handler — the button
  // reflects the vote immediately, reverted only if the request fails.
  // Voting again with the same rating clears it rather than re-sending it.
  const handleFeedback = (messageId: string, rating: "up" | "down") => {
    const current = messages.find((m) => m.id === messageId)?.feedback;
    const nextRating = current === rating ? null : rating;
    dispatch(setMessageFeedback({ id: messageId, feedback: nextRating }));
    if (nextRating) {
      sendDimeAiFeedback(messageId, nextRating).then((res) => {
        if (res?.status !== 200) {
          dispatch(setMessageFeedback({ id: messageId, feedback: null }));
        }
      });
    }
  };

  /**
   * Polls the stored thread until a reply lands after `question`, then shows
   * it. Gives up quietly when the conversation on screen is replaced (New
   * chat, another thread opened) or after WATCH_MAX_MS. For a first turn
   * there is no thread id yet, so the thread is found by its title, which is
   * the question - the same lookup DimeAI.tsx uses.
   */
  const watchForAnswer = async (
    question: string,
    knownThreadId: string | null,
    asked: number,
  ) => {
    const startedAt = Date.now();
    const wanted = question.trim();
    let id = knownThreadId;

    while (Date.now() - startedAt < WATCH_MAX_MS) {
      await new Promise((resolve) => setTimeout(resolve, WATCH_POLL_MS));
      if (conversation.current !== asked) return;

      if (!id) {
        const listed = await getDimeAiThreads();
        if (listed?.status === 400 || listed?.status === 404) break;
        const match = (listed?.data?.data || []).find(
          (t: DimeAIThread) =>
            (t.title || "").trim() === wanted && (t.message_count || 0) > 1,
        );
        id = match ? match.id : null;
        if (!id) continue;
      }

      const res = await getDimeAiThread(id);
      if (res?.status === 400 || res?.status === 404) break;
      if (conversation.current !== asked) return;

      const stored = (res?.data?.data?.messages || []).filter(
        (m: any) => m.role === "user" || m.role === "assistant",
      );
      const textOf = (m: any) =>
        (m.content || [])
          .filter((block: any) => block.type === "text")
          .map((block: any) => block.text)
          .join(" ");
      let at = -1;
      stored.forEach((m: any, i: number) => {
        if (m.role === "user" && textOf(m).trim() === wanted) at = i;
      });
      const reply = at >= 0 ? stored.slice(at + 1).find((m: any) => m.role === "assistant") : null;
      if (reply) {
        const { body, caveat } = splitCaveat(textOf(reply), reply.meta?.caveats);
        dispatch(setThreadId(id));
        dispatch(
          appendMessage({
            role: "assistant",
            content: body,
            caveat,
            id: reply.id,
            feedback: reply.feedback_rating ?? null,
          }),
        );
        loadThreads();
        dispatch(setAwaitingReply(false));
        return;
      }
    }

    // Never arrived in the time allowed: say so rather than spin forever.
    if (conversation.current === asked) {
      dispatch(setAwaitingReply(false));
      toast.error("DimeAI is taking too long. Please check History or try again.");
    }
  };

  const handleSend = async (text?: string) => {
    const messageText = (text ?? input).trim();
    if (!messageText || inFlight.current) return;
    inFlight.current = true;

    dispatch(appendMessage({ role: "user", content: messageText }));
    setInput("");
    dispatch(setAwaitingReply(true));

    const asked = conversation.current;

    const res = await sendDimeAiMessage(
      messageText,
      messages,
      threadId,
      companyId,
    );

    // The conversation this was asked in is gone (New chat pressed while
    // waiting) — appending now would put a reply on screen under a
    // question that's no longer there. Same drop-it rule as DimeAI.tsx.
    if (conversation.current !== asked) {
      inFlight.current = false;
      return;
    }

    // Still being answered: the request stopped waiting (timeout, a 502/503/504
    // from a proxy, a dropped connection), the server did not. Same recovery
    // the full DimeAI page has - keep the question and the indicator on
    // screen and watch the thread for the reply, instead of a toast and an
    // answer that only appears after a refresh.
    if (res?.data?.stillWorking) {
      toast.info?.(res.data.message);
      await watchForAnswer(messageText, threadId, asked);
      inFlight.current = false;
      return;
    }

    if (res?.status === 200 && res?.data?.success) {
      if (res.data.data.thread_id) dispatch(setThreadId(res.data.data.thread_id));
      const { body, caveat } = splitCaveat(
        res.data.data.reply,
        res.data.data.caveats,
      );
      dispatch(
        appendMessage({
          role: "assistant",
          content: body,
          caveat,
          id: res.data.data.message_id,
          feedback: null,
        }),
      );
      loadThreads();
    } else {
      const detail = res?.data?.message || "";
      // Same resolvable error DimeAI.tsx watches for: the backend asks
      // rather than guesses when someone belongs to several organisations.
      if (/several organisations|specify which organisation/i.test(detail)) {
        dispatch(setNeedsCompany(true));
      }
      toast.error(detail || "DimeAI couldn't respond. Please try again.");
    }
    inFlight.current = false;
    dispatch(setAwaitingReply(false));
  };

  const handleKeyDown = (e: React.KeyboardEvent<HTMLTextAreaElement>) => {
    if (e.key === "Enter" && !e.shiftKey) {
      e.preventDefault();
      handleSend();
    }
  };

  if (!shouldRender) return null;

  return (
    <div
      className="dimeai-widget"
      ref={widgetRef}
      style={
        position
          ? { right: `${position.right}px`, bottom: `${position.bottom}px` }
          : undefined
      }
    >
      {isOpen && (
        <div className="dimeai-widget-panel" role="dialog" aria-label="DimeAI chat">
          <div className="dimeai-widget-header">
            <div className="dimeai-widget-header-title">
              <DimeAiBrain className="dimeai-brain--sm" thinking={awaitingReply} />
              <span>DimeAI</span>
            </div>
            <div className="dimeai-widget-header-actions">
              <button
                type="button"
                className={`dimeai-widget-tool-btn${
                  showHistory ? " dimeai-widget-tool-btn--on" : ""
                }`}
                onClick={handleToggleHistory}
                aria-pressed={showHistory}
              >
                History
              </button>
              <button
                type="button"
                className="dimeai-widget-tool-btn"
                onClick={handleNewChat}
                disabled={!messages.length || awaitingReply}
              >
                New chat
              </button>
              <button
                type="button"
                className="dimeai-widget-close-btn"
                onClick={() => dispatch(closeWidget())}
                aria-label="Close DimeAI chat"
              >
                ×
              </button>
            </div>
          </div>

          {/* Always rendered — previously hidden behind
              `needsCompany || companyId || companies.length > 1`, which went
              false (and the whole section vanished) whenever a past
              conversation with no company_id of its own was reopened by
              someone with only one organisation to pick from. The section
              now always shows; only its contents change with the number of
              organisations the person has. */}
          <div className="dimeai-widget-company">
            <label>
              Organisation
              {companies.length > 1 ? (
                <select
                  value={companyId ?? ""}
                  onChange={(e) => chooseCompany(e.target.value)}
                  aria-label="Organisation to ask about"
                  disabled={awaitingReply}
                >
                  <option value="">Select…</option>
                  {companies.map((c) => (
                    <option key={c.id} value={c.id}>
                      {c.name}
                    </option>
                  ))}
                </select>
              ) : (
                <span className="dimeai-widget-company-value">
                  {companies[0]?.name ?? ""}
                </span>
              )}
            </label>
          </div>

          {showHistory ? (
            <div className="dimeai-widget-history" aria-label="Past conversations">
              <div className="dimeai-widget-history-list">
                {threads.length === 0 && (
                  <p className="dimeai-widget-history-empty">
                    No past conversations yet.
                  </p>
                )}
                {threads.map((thread) => (
                  <button
                    key={thread.id}
                    type="button"
                    className={`dimeai-widget-history-item${
                      thread.id === threadId
                        ? " dimeai-widget-history-item--current"
                        : ""
                    }`}
                    onClick={() => openThread(thread)}
                  >
                    <span className="dimeai-widget-history-title">
                      {thread.title || "Untitled conversation"}
                    </span>
                    <span className="dimeai-widget-history-meta">
                      {companyName(thread.company_id)
                        ? `${companyName(thread.company_id)} · `
                        : ""}
                      {thread.message_count} message
                      {thread.message_count === 1 ? "" : "s"}
                      {thread.last_message_at
                        ? ` · ${new Date(thread.last_message_at).toLocaleDateString(
                            undefined,
                            { day: "numeric", month: "short" },
                          )}`
                        : ""}
                    </span>
                  </button>
                ))}
              </div>
            </div>
          ) : (
            <>
              <div className="dimeai-widget-thread" ref={scrollRef}>
                {messages.length === 0 && (
                  <div className="dimeai-widget-empty">
                    Ask about a balance, a variance, or anything in your
                    reconciliations — I can see the same ledger data DimeRec
                    and DimeXplain use.
                  </div>
                )}
                {messages.map((msg, idx) => (
                  <div
                    key={idx}
                    className={`dimeai-widget-bubble dimeai-widget-bubble--${msg.role}`}
                  >
                    {msg.role === "assistant" ? (
                      <>
                        <AnswerBody content={msg.content} />
                        {msg.caveat && (
                          <div className="dimeai-widget-caveat-group">
                            <p className="dimeai-widget-caveat">{msg.caveat}</p>
                          </div>
                        )}
                      </>
                    ) : (
                      msg.content
                    )}
                    {msg.role === "assistant" && msg.id && (
                      <div className="dimeai-widget-feedback-row">
                        <button
                          type="button"
                          aria-label="Good answer"
                          aria-pressed={msg.feedback === "up"}
                          className={`dimeai-widget-feedback-btn${
                            msg.feedback === "up"
                              ? " dimeai-widget-feedback-btn--active"
                              : ""
                          }`}
                          onClick={() => handleFeedback(msg.id as string, "up")}
                        >
                          &#128077;
                        </button>
                        <button
                          type="button"
                          aria-label="Bad answer"
                          aria-pressed={msg.feedback === "down"}
                          className={`dimeai-widget-feedback-btn${
                            msg.feedback === "down"
                              ? " dimeai-widget-feedback-btn--active"
                              : ""
                          }`}
                          onClick={() => handleFeedback(msg.id as string, "down")}
                        >
                          &#128078;
                        </button>
                      </div>
                    )}
                  </div>
                ))}
                {awaitingReply && (
                  <div className="dimeai-widget-bubble dimeai-widget-bubble--assistant dimeai-widget-bubble--typing">
                    <DimeAiBrain className="dimeai-brain--xs" thinking />
                    <span className="dimeai-widget-typing-text">{thinkingLabel}</span>
                  </div>
                )}
              </div>

              <div className="dimeai-widget-composer">
                {(documents.length > 0 || uploading) && (
                  <div
                    className="dimeai-widget-attachments"
                    aria-label="Attached files"
                  >
                    {documents.map((document) => (
                      <span
                        key={document.id}
                        className={`dimeai-widget-attachment${
                          previewUrls[document.id]
                            ? " dimeai-widget-attachment--image"
                            : ""
                        }`}
                      >
                        {previewUrls[document.id] && (
                          <img
                            src={previewUrls[document.id]}
                            alt=""
                            className="dimeai-widget-attachment-thumb"
                          />
                        )}
                        <span className="dimeai-widget-attachment-name">
                          {document.filename}
                        </span>
                        <button
                          type="button"
                          className="dimeai-widget-attachment-remove"
                          onClick={() => handleDiscard(document)}
                          aria-label={`Remove ${document.filename}`}
                        >
                          &times;
                        </button>
                      </span>
                    ))}
                    {uploading && (
                      <span className="dimeai-widget-attachment dimeai-widget-attachment--busy">
                        Reading {uploading}&hellip;
                      </span>
                    )}
                  </div>
                )}

                <div className="dimeai-widget-input-row">
                  <input
                    ref={fileRef}
                    type="file"
                    multiple
                    className="dimeai-widget-file-input"
                    accept=".xlsx,.xls,.xlsm,.csv,.tsv,.pdf,.docx,.txt,.md,.html,.png,.jpg,.jpeg,.tiff,.tif,.bmp,.gif"
                    onChange={(e) => {
                      const files = Array.from(e.target.files || []);
                      if (files.length) handleAttachMany(files);
                      e.target.value = "";
                    }}
                  />
                  <button
                    type="button"
                    className="dimeai-widget-attach-btn"
                    onClick={() => fileRef.current?.click()}
                    disabled={awaitingReply || Boolean(uploading)}
                    aria-label="Attach a file"
                  >
                    📎
                  </button>
                  <textarea
                    ref={inputRef}
                    value={input}
                    onChange={(e) => setInput(e.target.value)}
                    onKeyDown={handleKeyDown}
                    onPaste={(e) => {
                      // Same attach path as the button -- a pasted
                      // screenshot is a clipboard file, not text.
                      if (awaitingReply || uploading !== null) return;
                      const files = Array.from(e.clipboardData?.items || [])
                        .filter((item) => item.kind === "file")
                        .map((item) => item.getAsFile())
                        .filter((f): f is File => f !== null);
                      if (files.length) {
                        e.preventDefault();
                        handleAttachMany(files);
                      }
                    }}
                    placeholder="Message DimeAI…"
                    rows={1}
                    disabled={awaitingReply}
                  />
                  <button
                    type="button"
                    className="dimeai-widget-send-btn"
                    onClick={() => handleSend()}
                    disabled={!input.trim() || awaitingReply}
                    aria-label="Send"
                  >
                    Send
                  </button>
                </div>
              </div>
            </>
          )}
        </div>
      )}

      {/* Shown only while collapsed — the brain alone reads as ambiguous
          to a first-time user, per feedback. Hides the moment the panel is
          open (the chat itself is the context at that point) and comes
          back as soon as it's collapsed again. Not a button — it rides
          along on the actual bubble underneath for click/drag purposes. */}
      {!isOpen && (
        <span className="dimeai-widget-label" aria-hidden="true">
          Ask DimeAI
        </span>
      )}

      <button
        type="button"
        className="dimeai-widget-bubble-btn"
        onPointerDown={handleBubblePointerDown}
        onClick={handleBubbleClick}
        aria-label={isOpen ? "Close DimeAI chat" : "Open DimeAI chat"}
        aria-expanded={isOpen}
      >
        {isOpen ? (
          <span className="dimeai-widget-bubble-close">×</span>
        ) : (
          <DimeAiBrain thinking={awaitingReply} />
        )}
      </button>
    </div>
  );
};

export default DimeAiWidget;
